import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  InputNumber, 
  message, 
  Typography,
  Tag,
  Space,
  Popconfirm,
  Row,
  Col,
  Descriptions,
  Statistic,
  Spin,
  Divider
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BarChartOutlined, EyeOutlined, FilePdfOutlined, FileExcelOutlined } from '@ant-design/icons';
import { stationOwnerAPI, analyticsAPI } from '../services/api';
import { handleExport } from '../utils/exportUtils';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Campaign {
  _id: string;
  title: string;
  description: string;
  station: {
    _id: string;
    name: string;
  };
  category: {
    type: string;
    name: string;
  };
  discountType: string;
  discountValue: number;
  minPurchaseAmount: number;
  maxDiscountAmount?: number;
  startDate: string;
  endDate: string;
  status: string;
  isActive: boolean;
  usageLimit?: number;
  usedCount: number;
  totalViews?: number;
  totalUses?: number;
}

interface Station {
  _id: string;
  name: string;
}

const Campaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [form] = Form.useForm();
  const [performanceModalVisible, setPerformanceModalVisible] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [roiData, setRoiData] = useState<any>(null);
  const [loadingROI, setLoadingROI] = useState(false);

  useEffect(() => {
    fetchCampaigns();
    fetchStations();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await stationOwnerAPI.getCampaigns();
      setCampaigns(response);
    } catch (error) {
      message.error('Kampanyalar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const handleAddCampaign = () => {
    setEditingCampaign(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditCampaign = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    form.setFieldsValue({
      title: campaign.title,
      description: campaign.description,
      station: campaign.station._id,
      category: campaign.category.type,
      discountType: campaign.discountType,
      discountValue: campaign.discountValue,
      minPurchaseAmount: campaign.minPurchaseAmount,
      maxDiscountAmount: campaign.maxDiscountAmount,
      startDate: dayjs(campaign.startDate),
      endDate: dayjs(campaign.endDate),
      usageLimit: campaign.usageLimit,
      image: null
    });
    setModalVisible(true);
  };

  const handleViewPerformance = async (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setPerformanceModalVisible(true);
    setLoadingROI(true);
    try {
      const data = await analyticsAPI.getCampaignROI(campaign._id);
      setRoiData(data);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Performans verileri yüklenemedi');
    } finally {
      setLoadingROI(false);
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    try {
      await stationOwnerAPI.deleteCampaign(campaignId);
      message.success('Kampanya başarıyla silindi');
      fetchCampaigns();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const campaignData = {
        title: values.title,
        description: values.description,
        stationId: values.station,
        category: values.category,
        discountType: values.discountType,
        discountValue: values.discountValue,
        minPurchaseAmount: values.minPurchaseAmount || 0,
        maxDiscountAmount: values.maxDiscountAmount || null,
        startDate: values.startDate.toISOString(),
        endDate: values.endDate.toISOString(),
        usageLimit: values.usageLimit || null,
        image: values.image || null
      };

      if (editingCampaign) {
        await stationOwnerAPI.updateCampaign(editingCampaign._id, campaignData);
        message.success('Kampanya güncellendi');
      } else {
        await stationOwnerAPI.addCampaign(campaignData);
        message.success('Kampanya eklendi ve onay bekliyor');
      }

      setModalVisible(false);
      form.resetFields();
      setEditingCampaign(null);
      fetchCampaigns();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'pending_approval': return 'orange';
      case 'rejected': return 'red';
      case 'expired': return 'gray';
      default: return 'blue';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'pending_approval': return 'Onay Bekliyor';
      case 'rejected': return 'Reddedildi';
      case 'expired': return 'Süresi Doldu';
      default: return status;
    }
  };

  const columns = [
    {
      title: 'Kampanya Adı',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
      render: (text: string) => (
        <div 
          style={{ 
            maxWidth: '180px', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={text}
        >
          {text}
        </div>
      ),
    },
    {
      title: 'İstasyon',
      dataIndex: ['station', 'name'],
      key: 'station',
      width: 150,
      ellipsis: true,
      render: (text: string) => (
        <div 
          style={{ 
            maxWidth: '130px', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={text}
        >
          {text}
        </div>
      ),
    },
    {
      title: 'Kategori',
      dataIndex: ['category', 'name'],
      key: 'category',
      width: 120,
      render: (text: string) => (
        <Tag color="blue" style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {text}
        </Tag>
      ),
    },
    {
      title: 'İndirim',
      key: 'discount',
      width: 100,
      render: (record: Campaign) => (
        <div style={{ fontSize: '13px', fontWeight: 'bold' }}>
          {record.discountType === 'percentage' ? (
            <span style={{ color: '#52c41a' }}>%{record.discountValue}</span>
          ) : (
            <span style={{ color: '#52c41a' }}>{record.discountValue}₺</span>
          )}
        </div>
      ),
    },
    {
      title: 'Tarih',
      key: 'dateRange',
      width: 120,
      render: (record: Campaign) => (
        <div style={{ fontSize: '12px' }}>
          <div style={{ fontWeight: 'bold' }}>{dayjs(record.startDate).format('DD.MM')}</div>
          <div style={{ color: '#666' }}>
            {dayjs(record.endDate).format('DD.MM')}
          </div>
        </div>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} style={{ fontSize: '11px' }}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Kullanım',
      key: 'usage',
      width: 100,
      render: (record: Campaign) => (
        <div style={{ fontSize: '12px', textAlign: 'center' }}>
          <div>{record.usedCount || 0}/{record.usageLimit || '∞'}</div>
          {record.totalViews && (
            <div style={{ color: '#999', fontSize: '10px' }}>
              {record.totalViews} görüntüleme
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Performans',
      key: 'performance',
      width: 120,
      render: (record: Campaign) => {
        const usageRate = record.totalViews && record.totalViews > 0
          ? ((record.usedCount || 0) / record.totalViews * 100).toFixed(1)
          : 0;
        return (
          <div style={{ fontSize: '12px' }}>
            <div>Kullanım: {usageRate}%</div>
            <Button
              type="link"
              size="small"
              icon={<BarChartOutlined />}
              onClick={() => handleViewPerformance(record)}
              style={{ padding: 0, height: 'auto' }}
            >
              Detay
            </Button>
          </div>
        );
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (record: Campaign) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditCampaign(record)}
            style={{ fontSize: '11px' }}
          >
            Düzenle
          </Button>
          <Popconfirm
            title={
              record.status === 'active' 
                ? "Aktif kampanyayı pasif yapmak istediğinizden emin misiniz?" 
                : "Bu kampanyayı silmek istediğinizden emin misiniz?"
            }
            onConfirm={() => handleDeleteCampaign(record._id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              style={{ fontSize: '11px' }}
            >
              {record.status === 'active' ? 'Pasif' : 'Sil'}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '8px' }}>
        <Title level={2} style={{ margin: 0 }}>
          Kampanyalarım
        </Title>
        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddCampaign}
          >
            Yeni Kampanya Oluştur
          </Button>
          <Button 
            icon={<FilePdfOutlined />}
            onClick={() => handleExport(
              () => stationOwnerAPI.exportCampaignsPDF(),
              `kampanyalar_${Date.now()}.pdf`,
              () => message.success('PDF başarıyla indirildi')
            )}
          >
            PDF İndir
          </Button>
          <Button 
            icon={<FileExcelOutlined />}
            onClick={() => handleExport(
              () => stationOwnerAPI.exportCampaignsExcel(),
              `kampanyalar_${Date.now()}.xlsx`,
              () => message.success('Excel başarıyla indirildi')
            )}
          >
            Excel İndir
          </Button>
        </Space>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={campaigns}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kampanya`,
            responsive: true
          }}
        />
      </Card>

      <Modal
        title={editingCampaign ? 'Kampanya Düzenle' : 'Yeni Kampanya Oluştur'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="title"
            label="Kampanya Başlığı"
            rules={[{ required: true, message: 'Kampanya başlığı gerekli' }]}
          >
            <Input placeholder="Kampanya başlığını girin" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Açıklama"
            rules={[{ required: true, message: 'Açıklama gerekli' }]}
          >
            <TextArea placeholder="Kampanya açıklamasını girin" rows={4} />
          </Form.Item>

          <Form.Item
            name="station"
            label="İstasyon"
            rules={[{ required: true, message: 'İstasyon seçin' }]}
          >
            <Select placeholder="İstasyon seçin">
              {stations.map(station => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="category"
            label="Kategori"
            rules={[{ required: true, message: 'Kategori seçin' }]}
          >
            <Select placeholder="Kategori seçin">
              <Option value="fuel">Yakıt</Option>
              <Option value="market">Market</Option>
              <Option value="car_wash">Oto Yıkama</Option>
              <Option value="restaurant">Restoran</Option>
              <Option value="atm">ATM</Option>
              <Option value="ev_charging">Elektrikli Araç Şarjı</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="discountType"
                label="İndirim Türü"
                rules={[{ required: true, message: 'İndirim türü seçin' }]}
              >
                <Select placeholder="İndirim türü">
                  <Option value="percentage">Yüzde (%)</Option>
                  <Option value="fixed_amount">Sabit Tutar (₺)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="discountValue"
                label="İndirim Değeri"
                rules={[{ required: true, message: 'İndirim değeri gerekli' }]}
              >
                <InputNumber
                  placeholder="İndirim değeri"
                  style={{ width: '100%' }}
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startDate"
                label="Başlangıç Tarihi"
                rules={[{ required: true, message: 'Başlangıç tarihi gerekli' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="endDate"
                label="Bitiş Tarihi"
                rules={[{ required: true, message: 'Bitiş tarihi gerekli' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="usageLimit"
            label="Kullanım Limiti (Boş bırakırsanız sınırsız)"
          >
            <InputNumber
              placeholder="Kullanım limiti"
              style={{ width: '100%' }}
              min={1}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingCampaign ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Performans Modal */}
      <Modal
        title={
          <Space>
            <BarChartOutlined />
            <span>Kampanya Performans Analizi</span>
          </Space>
        }
        open={performanceModalVisible}
        onCancel={() => {
          setPerformanceModalVisible(false);
          setSelectedCampaign(null);
          setRoiData(null);
        }}
        footer={null}
        width={800}
      >
        {loadingROI ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : roiData ? (
          <div>
            <Descriptions title={selectedCampaign?.title} bordered column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Durum">
                <Tag color={getStatusColor(selectedCampaign?.status || '')}>
                  {getStatusText(selectedCampaign?.status || '')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Kategori">
                {selectedCampaign?.category.name}
              </Descriptions.Item>
              <Descriptions.Item label="Başlangıç Tarihi">
                {dayjs(selectedCampaign?.startDate).format('DD MMMM YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Bitiş Tarihi">
                {dayjs(selectedCampaign?.endDate).format('DD MMMM YYYY')}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Kullanım İstatistikleri</Divider>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic
                  title="Toplam Görüntüleme"
                  value={roiData.usageStats?.totalViews || 0}
                  prefix={<EyeOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Toplam Kullanım"
                  value={roiData.usageStats?.totalUses || 0}
                  prefix={<BarChartOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Kullanım Oranı"
                  value={roiData.usageStats?.usageRate || 0}
                  suffix="%"
                  precision={1}
                />
              </Col>
            </Row>

            {roiData.usageStats?.remainingUses !== null && (
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={24}>
                  <Card>
                    <Statistic
                      title="Kalan Kullanım Hakkı"
                      value={roiData.usageStats.remainingUses}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            <Divider>Finansal Analiz</Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title="Tahmini Gelir"
                  value={roiData.financial?.estimatedRevenue || 0}
                  prefix={<Typography.Text type="success">₺</Typography.Text>}
                  precision={2}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Tahmini İndirim"
                  value={roiData.financial?.estimatedDiscount || 0}
                  prefix={<Typography.Text type="warning">₺</Typography.Text>}
                  precision={2}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Net Gelir"
                  value={roiData.financial?.netRevenue || 0}
                  prefix={<Typography.Text type="success">₺</Typography.Text>}
                  precision={2}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Card>
                  <Statistic
                    title="ROI (Yatırım Getirisi)"
                    value={roiData.financial?.roi || 0}
                    suffix="%"
                    precision={2}
                    valueStyle={{ 
                      color: parseFloat(roiData.financial?.roi || 0) > 0 ? '#52c41a' : '#ff4d4f',
                      fontSize: '24px'
                    }}
                  />
                </Card>
              </Col>
            </Row>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Typography.Text type="secondary">Veri bulunamadı</Typography.Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Campaigns;
